// types.ts - Type definitions for the Dance Video Segmenter with Sharing Limits

import type { Timestamp } from 'firebase/firestore';

export interface Segment {
  id: string;
  name: string;
  startTime: number;
  endTime: number;
  color: string;
  transitionDuration: number;
  isLearned?: boolean;
  isDisabled?: boolean;
  // Plain-segment title marker. Positional practice IDs are derived from
  // timeline order, so this preserves user-authored titles separately.
  segmentNameEdited?: boolean;
  // Combo model: a combo is a derived segment built from other segments.
  // `sourceSegmentIds` records its members and `originSegmentId` the segment it
  // was first built from (used for its colour). Absent on plain segments.
  // `comboNameEdited` marks a user-authored combo name so edge drags do not
  // replace it with the generated "Combo N-N" label.
  // Legacy merged segments (name contains '_') have none of these and are
  // detected via the `isCombo` fallback until migrated.
  isCombo?: boolean;
  sourceSegmentIds?: string[];
  originSegmentId?: string;
  comboNameEdited?: boolean;
}

export type ComboEdge = 'left' | 'right';

export type TimelineLanePlacement = 'over-video' | 'under-video';

export const VIDEO_LEARNING_STATUS_VALUES = ['queued', 'learning', 'learned'] as const;

export type VideoLearningStatus = typeof VIDEO_LEARNING_STATUS_VALUES[number];

/**
 * Normalized crop rectangle for the visible video-content frame.
 *
 * Coordinates are percentages in the range 0..1 and are relative to the actual
 * media content, not any black letterbox/pillarbox area added by object-fit or
 * the YouTube iframe container. Missing settings.viewCrop represents full frame.
 */
export interface VideoViewCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

const VIDEO_VIEW_CROP_EPSILON = 0.000001;

const isFiniteCropNumber = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value)
);

export const isValidVideoViewCrop = (value: unknown): value is VideoViewCrop => {
  if (!value || typeof value !== 'object') return false;

  const crop = value as Partial<VideoViewCrop>;
  const { x, y, width, height } = crop;

  if (
    !isFiniteCropNumber(x) ||
    !isFiniteCropNumber(y) ||
    !isFiniteCropNumber(width) ||
    !isFiniteCropNumber(height)
  ) {
    return false;
  }

  if (x < 0 || y < 0 || width <= 0 || height <= 0) return false;
  if (x > 1 || y > 1 || width > 1 || height > 1) return false;

  return (
    x + width <= 1 + VIDEO_VIEW_CROP_EPSILON &&
    y + height <= 1 + VIDEO_VIEW_CROP_EPSILON
  );
};

export const isFullFrameVideoViewCrop = (value: unknown): boolean => {
  if (!isValidVideoViewCrop(value)) return false;

  return (
    Math.abs(value.x) <= VIDEO_VIEW_CROP_EPSILON &&
    Math.abs(value.y) <= VIDEO_VIEW_CROP_EPSILON &&
    Math.abs(value.width - 1) <= VIDEO_VIEW_CROP_EPSILON &&
    Math.abs(value.height - 1) <= VIDEO_VIEW_CROP_EPSILON
  );
};

export const normalizeVideoViewCrop = (value: unknown): VideoViewCrop | undefined => {
  if (!isValidVideoViewCrop(value) || isFullFrameVideoViewCrop(value)) {
    return undefined;
  }

  return {
    x: value.x,
    y: value.y,
    width: value.width,
    height: value.height
  };
};

export const normalizeVideoSettingsViewCrop = <T extends object>(
  settings: T
): Omit<T, 'viewCrop'> & { viewCrop?: VideoViewCrop } => {
  const normalizedSettings = { ...(settings as object) } as Omit<T, 'viewCrop'> & { viewCrop?: VideoViewCrop };
  const normalizedCrop = normalizeVideoViewCrop((settings as T & { viewCrop?: unknown }).viewCrop);

  if (normalizedCrop) {
    normalizedSettings.viewCrop = normalizedCrop;
  } else {
    delete normalizedSettings.viewCrop;
  }

  return normalizedSettings;
};

export interface ComboRange {
  sourceSegmentIds: string[];
  sourceSegments: Segment[];
  startTime: number;
  endTime: number;
  startIndex: number;
  endIndex: number;
  duration: number;
  color: string;
  originSegmentId?: string;
}

export interface VideoSettings {
  defaultSpeed: number;
  defaultMirror: boolean;
  autoLoop: boolean;
  transitionType: string;
  defaultMuted?: boolean;
  segmentOverlapSeconds?: number;
  viewCrop?: VideoViewCrop;
  /** Timeline zoom level (1 = whole video fits). Missing means 1. */
  timelineZoom?: number;
  /** Segment or combo that was selected (and looping, if autoLoop) when last saved; reopened on
   *  load like a curated default combo. Null/missing means none. */
  activeSegmentId?: string | null;
}

export interface CheckpointData {
  filename: string;
  segments: Segment[];
  settings: VideoSettings;
}

export interface VideoState {
  videoSrc: string | null;
  videoFileName: string;
  isLoading: boolean;
  loadingSource: 'local' | 'firebase' | 'demo' | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  playbackRate: number;
  isMirrored: boolean;
  isMuted: boolean;
  userMutePreference: boolean;
  viewCrop?: VideoViewCrop;
  /** Timeline zoom level (1 = whole video fits). Missing means 1. */
  timelineZoom?: number;
}

export interface SegmentState {
  segments: Segment[];
  activeSegment: string | null;
  activeComboId: string | null;
  isMarkingSegment: boolean;
  currentSegmentStart: number | null;
  isLooping: boolean;
  transitionType: string;
  transitionDuration: number;
  // Preroll overlap in seconds before the active segment.
  segmentOverlap: number;
}

export interface UIState {
  editingSegment: string | null;
  isFullscreen: boolean;
  practiceMode: boolean;
  showMergedTimeline: boolean;
  showSettingsPopup: boolean;
  customSpeed: string;
  isCustomSpeed: boolean;
  isDemo: boolean;
}

export interface DemoData {
  videoFile: string;
  title: string;
  description: string;
  segments: Segment[];
  settings: VideoSettings;
}

export interface SessionData {
  sessionId: string;
  videoUrl: string;
  videoPath: string;
  segments: Segment[];
  settings: VideoSettings;
  filename: string;
  createdAt: Date | string;
  expiresAt: Date | string;
}

// ============================================
// SHARING FEATURE TYPES - NEW
// ============================================

/**
 * Sharing limits constants
 */
export const SHARE_LIMITS = {
  MAX_VIEWS_PER_SHARE: 30,
  SHARE_EXPIRY_DAYS: 7,
  MAX_ACTIVE_SHARES_PER_USER: 1
} as const;

/**
 * Reasons why a share can be deactivated
 */
export type ShareDeactivationReason = 'expired' | 'max_views' | 'user_revoked';

/**
 * Share status for UI display
 */
export type ShareStatus = 'active' | 'near_limit' | 'expired' | 'maxed' | 'none';

/**
 * Extended user profile with sharing tracking
 */
export interface UserProfile {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  libraryCount: number;
  libraryLimit: number;
  isPremium: boolean;
  storageExpiresAt?: Date;
  createdAt: Date;
  lastLogin: Date;
  authProvider: string;
  
  // NEW: Sharing tracking fields
  activeSharesCount: number;      // Current number of active shares (max 1)
  lastShareCreatedAt?: Date;      // Timestamp of last share creation
  totalSharesCreated?: number;    // Lifetime count of shares created
  totalShareViews?: number;       // Lifetime count of share views received
}

/**
 * Shared session with view and expiry limits
 */
export interface SharedSession {
  shareId: string;
  ownerId: string;                // User who created the share
  videoId: string;                // Reference to the video in user's library
  videoStoragePath: string;       // Full storage path for video file
  title: string;
  segments: Segment[];
  settings: VideoSettings;
  
  // Timestamps
  createdAt: Date;
  expiresAt: Date;                // 7 days from creation
  
  // View tracking
  accessCount: number;            // Current number of views
  maxViews: number;               // Maximum views allowed (30)
  
  // Status
  isActive: boolean;              // false when any limit is hit
  deactivatedReason?: ShareDeactivationReason;
  deactivatedAt?: Date;
  
  // Optional analytics
  lastAccessedAt?: Date;
  uniqueViewers?: string[];       // Array of anonymized viewer IDs (optional)
}

/**
 * Video metadata with share tracking
 */
export interface VideoMeta {
  id: string;
  storagePath: string;
  title: string;
  size: number;
  segments: Segment[];
  settings: VideoSettings;
  learningStatus?: VideoLearningStatus;
  playlistId?: string;
  createdAt: any;
  updatedAt: any;
  
  // Share information
  currentShareId?: string | null;
  currentShareUrl?: string | null;
  currentShareExpiresAt?: any | null;
  currentShareViewCount?: number;       // Current views on active share
  currentShareMaxViews?: number;        // Max views for active share (30)
  currentShareIsActive?: boolean;       // Whether the share is still valid
  currentShareCreatedAt?: any | null;   // When current share was created
  
  // Share analytics (optional)
  totalSharesCreated?: number;          // How many times this video was shared
  totalShareViews?: number;             // Total views received across all shares
}

/**
 * Share creation request
 */
export interface CreateShareRequest {
  videoId: string;
  videoStoragePath: string;
  title: string;
  segments: Segment[];
  settings: VideoSettings;
  ownerId: string;
}

/**
 * Share creation response
 */
export interface CreateShareResponse {
  shareId: string;
  shareUrl: string;
  expiresAt: Date;
  maxViews: number;
  createdAt: Date;
}

/**
 * Share status check response
 */
export interface ShareStatusCheck {
  hasActiveShare: boolean;
  shareId?: string;
  shareUrl?: string;
  status?: ShareStatus;
  viewsRemaining?: number;
  daysRemaining?: number;
  expiresAt?: Date;
  reason?: string;
}

/**
 * Share limit check response
 */
export interface ShareLimitCheck {
  canShare: boolean;
  reason?: 'max_views_reached' | 'expired' | 'max_active_shares' | null;
  existing?: SharedSession;
  viewsRemaining?: number;
  daysRemaining?: number;
}

/**
 * Share metrics for analytics
 */
export interface ShareMetrics {
  totalViews: number;
  uniqueViewers: number;
  viewTimestamps: Date[];
  peakViewTime?: Date;
  averageViewsPerDay: number;
}

/**
 * Firestore document data types (with Timestamp)
 * These use the imported Timestamp type from firebase/firestore
 */
export interface SharedSessionFirestore {
  shareId: string;
  ownerId: string;
  videoId: string;
  videoStoragePath: string;
  title: string;
  segments: Segment[];
  settings: VideoSettings;
  createdAt: Timestamp;
  expiresAt: Timestamp;
  accessCount: number;
  maxViews: number;
  isActive: boolean;
  deactivatedReason?: ShareDeactivationReason;
  deactivatedAt?: Timestamp;
  lastAccessedAt?: Timestamp;
  uniqueViewers?: string[];
}

export interface UserProfileFirestore {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  libraryCount: number;
  libraryLimit: number;
  isPremium: boolean;
  storageExpiresAt?: Timestamp;
  createdAt: Timestamp;
  lastLogin: Timestamp;
  authProvider: string;
  activeSharesCount: number;
  lastShareCreatedAt?: Timestamp;
  totalSharesCreated?: number;
  totalShareViews?: number;
}

export interface VideoMetaFirestore {
  id: string;
  storagePath: string;
  title: string;
  size: number;
  segments: Segment[];
  settings: VideoSettings;
  learningStatus?: VideoLearningStatus;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  currentShareId?: string | null;
  currentShareUrl?: string | null;
  currentShareExpiresAt?: Timestamp | null;
  currentShareViewCount?: number;
  currentShareMaxViews?: number;
  currentShareIsActive?: boolean;
  currentShareCreatedAt?: Timestamp | null;
  totalSharesCreated?: number;
  totalShareViews?: number;
}


/**
 * Video metadata with thumbnail support
 */
export interface VideoMeta {
  id: string;
  storagePath: string;
  thumbnailPath?: string;  // NEW: Path to thumbnail in Firebase Storage
  title: string;
  size: number;
  segments: Segment[];
  settings: VideoSettings;
  learningStatus?: VideoLearningStatus;
  createdAt: any;
  updatedAt: any;
  
  // Share information
  currentShareId?: string | null;
  currentShareUrl?: string | null;
  currentShareExpiresAt?: any | null;
  currentShareViewCount?: number;
  currentShareMaxViews?: number;
  currentShareIsActive?: boolean;
  currentShareCreatedAt?: any | null;
  
  // Share analytics (optional)
  totalSharesCreated?: number;
  totalShareViews?: number;
}

// Helper type for thumbnail loading state
export interface ThumbnailState {
  [videoId: string]: {
    url?: string;
    loading: boolean;
    error?: string;
  };
}

// ============================================
// PRACTICE PROJECT TYPES — V2 Upload Projects
// ============================================

export interface UploadProjectSource {
  type: 'upload';
  storagePath: string;
  uploadSize: number;
  duration?: number;
  thumbnailPath?: string;
}

export interface YouTubePracticeSource {
  type: 'youtube';
  videoId: string;
  canonicalUrl: string;
  startTime?: number;
}

export interface YouTubeProjectSource {
  type: 'youtube';
  videoId: string;
  canonicalUrl: string;
  startTime?: number;
  videoTitle?: string;
  thumbnailUrl?: string;
}

export interface LocalDeviceProjectSource {
  type: 'local-device';
  localUri: string;
  fileFingerprint?: string;
  size: number;
  duration?: number;
  thumbnailUri?: string;
}

export type PracticeProjectSource =
  | UploadProjectSource
  | YouTubeProjectSource
  | LocalDeviceProjectSource;

export interface PracticeProject {
  id: string;
  schemaVersion: 1;
  ownerId: string;
  source: PracticeProjectSource;
  title: string;
  segments: Segment[];
  settings: VideoSettings;
  learningStatus?: VideoLearningStatus;
  playlistId?: string;
  createdAt: any;
  updatedAt: any;
}

export interface LegacyVideoProjectSource {
  type: 'legacy-video';
  storagePath: string;
  size: number;
  duration?: number;
  thumbnailPath?: string;
}

export type NormalizedLibraryProjectSource =
  | UploadProjectSource
  | YouTubeProjectSource
  | LocalDeviceProjectSource
  | LegacyVideoProjectSource;

export type LibraryProjectMediaAvailabilityStatus =
  | 'cloud'
  | 'youtube'
  | 'device-local'
  | 'missing-cloud-media';

export interface LibraryProjectMediaAvailability {
  status: LibraryProjectMediaAvailabilityStatus;
  label: string;
  isPlayableInCurrentApp: boolean;
  isCrossDevicePlayable: boolean;
  requiresFirebaseMediaUrl: boolean;
}

/**
 * Persistence-facing library project contract. It represents every readable
 * library record as one practice project shape, regardless of whether the data
 * came from the current projects collection or the legacy videos collection.
 */
export interface NormalizedLibraryProject {
  id: string;
  source: NormalizedLibraryProjectSource;
  title: string;
  segments: Segment[];
  settings: VideoSettings;
  learningStatus?: VideoLearningStatus;
  playlistId?: string;
  createdAt: any;
  updatedAt: any;
  /** Local projects live in this browser's IndexedDB until sign-in migration. */
  persistence?: 'cloud' | 'local';

  currentShareId?: string | null;
  currentShareUrl?: string | null;
  currentShareExpiresAt?: any | null;
  currentShareViewCount?: number;
  currentShareMaxViews?: number;
  currentShareIsActive?: boolean;
  currentShareCreatedAt?: any | null;
  totalSharesCreated?: number;
  totalShareViews?: number;
}

/** Internal discriminator: which Firestore collection this item came from. */
export type LibraryItemCollection = 'projects' | 'videos';

/**
 * Normalized library item for the UI — covers both new project records and legacy
 * video records. The `_collection` field is internal-only; it must not be shown
 * to the user as language about "videos" vs "projects".
 * The `source` field is present for project records so callers can distinguish
 * upload-backed from YouTube-backed items and restore the correct player.
 */
export interface LibraryItem extends VideoMeta {
  _collection: LibraryItemCollection;
  source?: PracticeProjectSource;
}

/**
 * A small reference to a library item stored in a playlist membership document.
 * The item itself stays in users/{uid}/projects or users/{uid}/videos.
 */
export interface LibraryPlaylistMembership {
  id: string;
  playlistId: string;
  itemId: string;
  itemCollection: LibraryItemCollection;
  position: number;
  addedAt?: any;
  /** True when this record is synthesized from a legacy item's playlistId. */
  legacy?: boolean;
}

export interface LibraryPlaylist {
  id: string;
  title: string;
  ownerId: string;
  createdAt: any;
  updatedAt: any;
}
